import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepSkeleton } from '@components/BookingFlow/Common/BookingStepSkeleton';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { NoAvailableServicesMessage } from '@components/BookingFlow/Mobile/NoAvailableServicesMessage';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingAddress, BookingStoreCar, SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { compareLicensePlates, customerRoutes } from 'noddi-util';
import {
  commonTrackingEvents,
  tracking,
  useBookingAddress,
  useBookingCars,
  useIsAddonsAvailable,
  useIsMultipleCarsBooking,
  useIsSalesItemStepValid,
  useLocalizationLanguage
} from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useGetUnavailableSalesItems } from '../../../../hooks/useGetUnavailableSalesItems';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import { useNextStepAfterServicesPath } from '../../../../hooks/useNextStepAfterServicesPath';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { SalesItemsMultipleCarsSelector } from './SalesItemsMultipleCarsSelector';
import { ServiceItemsForSingleCar } from './ServiceItemsForSingleCar';

export const ServiceItemsForCars = () => {
  const navigate = useNavigate();

  const isSalesItemStepValid = useIsSalesItemStepValid();

  const { unavailableSalesItemsError } = useGetUnavailableSalesItems();

  const isAddonsAvailable = useIsAddonsAvailable();
  const nextStepAfterServicesPath = useNextStepAfterServicesPath();

  const selectedAddress = useBookingAddress();
  const selectedCars = useBookingCars();

  const navigateToNextStep = () => {
    tracking.track(commonTrackingEvents.salesItemsSelected, { numberOfCars: selectedCars.length });
    if (isAddonsAvailable) {
      return navigate(customerRoutes.newBookingAddons.getPath());
    }
    navigate(nextStepAfterServicesPath);
  };

  if (!selectedAddress || !selectedCars.length) {
    return <BookingStepUnavailablePage missing={!selectedAddress ? 'address' : 'car'} />;
  }

  return (
    <BookingScreen
      progress={getMobileBookingProgress('services')}
      // oxlint-disable-next-line typescript/no-misused-promises -- async onNextClicked runs navigateToNextStep(); React discards the returned promise, so a rejection is unhandled unless the body catches
      onNextClicked={navigateToNextStep}
      disableNextButton={!isSalesItemStepValid}
      validationVariant='serviceSelectionRequired'
      title={t`What can we help you with?`}
    >
      {unavailableSalesItemsError ? (
        <ApiErrorMessageWithTrans error={unavailableSalesItemsError} />
      ) : (
        <SalesItemsSelector selectedAddress={selectedAddress} selectedCars={selectedCars} />
      )}
    </BookingScreen>
  );
};

const SalesItemsSelector = ({
  selectedAddress,
  selectedCars
}: {
  selectedAddress: BookingAddress;
  selectedCars: BookingStoreCar[];
}) => {
  const isMultipleCarsBooking = useIsMultipleCarsBooking();
  const { currentBrand } = useCurrentBrand();
  const { userGroupId } = useMobileBookingCustomer();
  const language = useLocalizationLanguage();

  const {
    data: carsWithSalesItems,
    isPending: isSalesItemsPending,
    error: salesItemsError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.getInitialAvailableSalesItems,
    input: {
      userGroupId,
      addressId: selectedAddress.id,
      licensePlates: selectedCars.map((car) => ({
        number: car.licensePlate.number,
        countryCode: car.licensePlate.countryCode
      })),
      brandDomain: currentBrand,
      language
    }
  });
  // Each category card reads these too (same query key). Fetched here in parallel with the services, so the step
  // swaps one skeleton for the cards instead of chaining a second skeleton per card after the first.
  const { isPending: isBookingCategoriesPending } = noddiAsync.useGet({
    type: URLKeys.getBookingCategoriesForNewBooking,
    input: { addressId: selectedAddress.id, brandDomain: currentBrand }
  });

  if (isSalesItemsPending || isBookingCategoriesPending) {
    return <BookingStepSkeleton rows={2} rowClassName='h-36' />;
  }

  const noServicesProps = {
    carCount: selectedCars.length,
    addressId: selectedAddress.id,
    licensePlateNumbers: selectedCars.map((c) => c.licensePlate.number)
  };

  if (salesItemsError) {
    return <NoAvailableServicesMessage error={salesItemsError} {...noServicesProps} />;
  }

  // Pair each returned car with the one in the draft, so the single-car view gets a car it can
  // count on rather than looking it up and handling a miss it cannot do anything about.
  const carsInBooking = carsWithSalesItems.data.cars.flatMap((salesItemsCar) => {
    const car = selectedCars.find((selectedCar) =>
      compareLicensePlates(selectedCar.licensePlate, salesItemsCar.licensePlate)
    );
    return car ? [{ salesItemsCar, car }] : [];
  });

  // A successful call that returns no cars at all is a backend anomaly, but the customer sees
  // the same "nothing bookable here" notice either way.
  if (carsInBooking.length === 0) {
    return <NoAvailableServicesMessage {...noServicesProps} />;
  }

  return (
    <div className='flex flex-col gap-6'>
      {isMultipleCarsBooking ? (
        <SalesItemsMultipleCarsSelector
          availableSalesItems={carsWithSalesItems.data}
          salesItemType={SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY}
        />
      ) : (
        carsInBooking.map(({ salesItemsCar, car }) => (
          <ServiceItemsForSingleCar key={salesItemsCar.licensePlate.number} salesItemsCar={salesItemsCar} car={car} />
        ))
      )}
    </div>
  );
};
